import { useCallback, useEffect, useState } from 'react';

export const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

/**
 * Desktop sidebar collapse state, persisted across reloads.
 *
 * Same shape as ThemeContext: a lazy useState initializer reads storage once on
 * mount, a useEffect writes it back on every change. It lives here rather than
 * inside Layout so it can be tested without mounting the whole shell, and it is
 * deliberately NOT a context — Layout is the only reader.
 *
 * The read is exact-match on '1'. Anything else — an empty string, a stale JSON
 * blob, a value some other build wrote — means expanded, because the failure a
 * user cannot explain is arriving to a rail they never asked for. Degrading to
 * the visible sidebar is always the safe direction.
 *
 * Collapse is a desktop concern only; mobile keeps its drawer and never calls this.
 */
export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
  );

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((c) => !c), []);

  return [collapsed, toggle];
}
